<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from '@/components/ui/toast'
import ReportsList from '@/components/report/ReportsList.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const reportUuid = computed(() => route.params.report_uuid)

// Get data from Vuex store
const reports = computed(() => {
  return store.getters['report/getOrgReports'](orgId.value)
})

const currentReport = computed(() => {
  if (!reportUuid.value) return null
  return store.getters['report/getReport'](orgId.value, reportUuid.value)
})

// Select a report - navigate to it
const selectReport = (report) => {
  console.log('Selecting report:', report)
  store.dispatch('report/setActiveReport', report.report_uuid)
  store.dispatch('report/setActiveVersion', null) // Reset to latest version
  router.push({
    name: 'ReportDetail',
    params: {
      org_id: orgId.value,
      report_uuid: report.report_uuid
    }
  })
}

// Delete report
const deleteReport = async (report) => {
  console.log('Deleting report:', report)

  try {
    await store.dispatch('report/deleteReportAction', {
      orgId: orgId.value,
      reportUuid: report.report_uuid
    })

    toast({
      title: 'Deleted',
      description: 'Report deleted',
      variant: 'default'
    })

    // If we deleted the current report, navigate back to list
    if (reportUuid.value === report.report_uuid) {
      router.push({ name: 'OrgReportsPage', params: { org_id: orgId.value } })
    }
  } catch (error) {
    console.error('Error deleting report:', error)
    toast({
      title: 'Error',
      description: 'Failed to delete report',
      variant: 'destructive'
    })
  }
}

// Fetch reports on mount
onMounted(async () => {
  console.log('Reports mounted, fetching reports for org:', orgId.value)

  try {
    await store.dispatch('report/fetchReports', { orgId: orgId.value })
  } catch (error) {
    console.error('Error fetching reports:', error)
    toast({
      title: 'Error',
      description: 'Failed to load reports',
      variant: 'destructive'
    })
  }
})
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="px-2 pb-2 border-b border-border flex-shrink-0">
      <h2 class="ml-3 text-sm font-semibold py-1">Reports</h2>
    </div>
    <ReportsList
      :reports="reports"
      :active-report="currentReport"
      @select="selectReport"
      @delete="deleteReport"
      class="flex-1 overflow-y-auto"
    />
  </div>
</template>
